import { Button, IconButton } from '@creditornot/cb-button';
import { FormHeader, FormRow, InputContainer } from '@creditornot/cb-form';
import { AlertCircleFilled, Trashbin } from '@creditornot/cb-icons';
import { cbSemanticCssVars, colors } from '@creditornot/cb-ingredients';
import { Input, InputAlert } from '@creditornot/cb-input';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Select } from '@creditornot/cb-select';
import { InputSwitch } from '@creditornot/cb-switch';
import { ValidationErrors } from 'final-form';
import arrayMutators from 'final-form-arrays';
import * as R from 'ramda';
import React, { useContext, useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { Option, OptionBinding } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';
import validateYupSchema from 'common/utils/validateYupSchema';

import { OptionBindingFormValues, RequiredSelection } from './types';
import { DEFAULT_OPTION, optionBindingToOptionBindingFormValues } from './utils';
import { MenuLocaleContext } from '../../../MenuLocaleContext';
import { MenuPermissionsContext } from '../../../components/MenuPermissionsContext';

type Props = {
  className?: string;
  onCancelEdit: () => void;
  onSubmit: (formValues: OptionBindingFormValues) => Promise<ValidationErrors | void>;
  option: Option;
  optionBinding: OptionBinding;
  otherItemOptions: Option[];
};

const Header = styled.span`
  color: ${cbSemanticCssVars.textPrimary};
`;

const CancelButton = styled(Button).attrs({ size: 'small', variant: 'secondary' })`
  margin-inline-end: 16px;
`;

const SaveButton = styled(Button).attrs({ size: 'small', variant: 'primary' })``;

const PaddedFormRow = styled(FormRow).attrs({ horizontalPadding: true })``;

const StyledInputContainer = styled(InputContainer)`
  position: relative;
  max-width: 392px;
`;

const ValidationError = styled(InputAlert).attrs({ variant: 'error' })`
  margin-block-start: 8px;
`;

const MaximumSelectionsTooltip = styled(LegacyPopover).attrs({ placement: 'right' })`
  position: absolute;
  height: 100%;
  right: -24px;
  top: 0;
  display: flex;
  align-items: center;
`;

const TooltipContent = styled(T.Body3)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  max-width: 325px;
  padding: 8px;
`;

const WarningIcon = styled(AlertCircleFilled).attrs({ fill: colors.strawberry100, width: 14, height: 14 })``;

const MaximumSelectionsList = styled.div`
  margin-block-start: 12px;
`;

const RequiredSelectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  padding: 12px 0;
  max-width: 392px;

  & + & {
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
`;

const RequiredSelectionRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  position: relative;

  & + & {
    margin-block-start: 12px;
  }
`;

const RequiredSelectionLabel = styled(T.Body3)`
  flex-shrink: 0;
  width: 48px;
`;

const RequiredSelectionSelect = styled(Select)`
  margin-inline-start: 12px;
  width: 100%;
`;

const AddRequiredSelection = styled(Button).attrs({ size: 'small', variant: 'secondary' })`
  margin-block-start: 12px;
`;

const RemoveRequiredSelection = styled(IconButton).attrs({ variant: 'neutral', size: 'small' })`
  position: absolute;
  right: -44px;
`;

const getOptionChoiceOptions = (selectedOptionId: string, otherItemOptions: Option[], primaryLanguage: Language) => {
  const selectedOption = otherItemOptions.find(o => o.id === selectedOptionId);

  return selectedOption
    ? selectedOption.values.map(choice => ({
        value: choice.id,
        label: localize(choice.name, primaryLanguage),
      }))
    : [];
};

const nonNegativeCountSchema = (intl: IntlShape) => {
  return yup
    .number()
    .integer(intl.formatMessage({ id: 'validation.mustBeInteger' }))
    .typeError(intl.formatMessage({ id: 'validation.mustBeNumber' }))
    .test('notNegative', intl.formatMessage({ id: 'validation.cantBeNegative' }), value => {
      if (value !== null && value !== undefined && value < 0) {
        return false;
      }
      return true;
    });
};

const maximumCountSchema = (intl: IntlShape) => {
  return nonNegativeCountSchema(intl).test('notZero', intl.formatMessage({ id: 'validation.cantBeZero' }), value => {
    if (value !== null && value !== undefined && value === 0) {
      return false;
    }
    return true;
  });
};

const OptionBindingForm = ({ className, onCancelEdit, onSubmit, option, optionBinding, otherItemOptions }: Props) => {
  const intl = useIntl();

  const menuPermissions = useContext(MenuPermissionsContext);
  const { primaryLanguage } = useContext(MenuLocaleContext);

  const optionName = localize(option.name, primaryLanguage);
  const optionChoicesWithMaxSelection = option.values.filter(
    optionChoice => optionChoice.multi_choice_config && optionChoice.multi_choice_config.selection_range.max !== 0,
  );

  const isMultichoiceOption = option.type === 'multi_choice' && optionBinding.multi_choice_config;

  const multiChoiceValidationSchema = useMemo(() => {
    return yup
      .object({
        maximumTotalSelections: maximumCountSchema(intl).required(intl.formatMessage({ id: 'validation.required' })),
        maximumSingleSelections: maximumCountSchema(intl)
          .max(
            yup.ref('maximumTotalSelections'),
            intl.formatMessage({ id: 'menu-option-instance.min-selections-validation' }),
          )
          .required(intl.formatMessage({ id: 'validation.required' })),
        minimumTotalSelections: nonNegativeCountSchema(intl)
          .max(
            yup.ref('maximumTotalSelections'),
            intl.formatMessage({ id: 'menu-option-instance.min-selections-validation' }),
          )
          .required(intl.formatMessage({ id: 'validation.required' })),
        freeSelections: nonNegativeCountSchema(intl).required(intl.formatMessage({ id: 'validation.required' })),
      })
      .required();
  }, [intl]);

  const requiredSelectionOptions = useMemo(
    () =>
      otherItemOptions.map(option => ({
        value: option.id,
        label: localize(option.name, primaryLanguage),
      })),
    [otherItemOptions, primaryLanguage],
  );

  return (
    <Form<OptionBindingFormValues>
      initialValues={optionBindingToOptionBindingFormValues(primaryLanguage, optionBinding, otherItemOptions)}
      initialValuesEqual={R.equals}
      mutators={{ ...arrayMutators }}
      onSubmit={onSubmit}
      validate={isMultichoiceOption ? validateYupSchema(multiChoiceValidationSchema) : undefined}
    >
      {({ handleSubmit, hasValidationErrors, pristine, submitting, values }) => (
        <form className={className}>
          <FormHeader
            horizontalPadding
            rightContent={
              <div>
                <CancelButton disabled={submitting} onClick={onCancelEdit} type="button">
                  <FormattedMessage id="state.cancel" />
                </CancelButton>
                <SaveButton
                  disabled={pristine || submitting || hasValidationErrors}
                  loading={submitting}
                  onClick={handleSubmit}
                  type="submit"
                >
                  <FormattedMessage id="state.save" />
                </SaveButton>
              </div>
            }
          >
            <Header>
              <FormattedMessage id="menu-option.details-header" />
            </Header>
          </FormHeader>

          <PaddedFormRow label={intl.formatMessage({ id: 'menu-option-instance.name' })}>
            <Field<OptionBindingFormValues['name']> name="name" parse={v => v}>
              {({ input }) => (
                <StyledInputContainer
                  hint={intl.formatMessage({
                    id:
                      input.value === optionName
                        ? 'menu-option-instance.warnSameAsOriginal'
                        : 'menu-option-instance.leaveEmptyForDefault',
                  })}
                >
                  <Input {...input} maxLength={50} placeholder={optionName} />
                </StyledInputContainer>
              )}
            </Field>
          </PaddedFormRow>

          {menuPermissions.posIdField && (
            <PaddedFormRow label={intl.formatMessage({ id: 'menu.pos-id' })}>
              <Field<OptionBindingFormValues['externalId']> name="externalId" parse={v => v}>
                {({ input }) => (
                  <StyledInputContainer
                    hint={intl.formatMessage({
                      id:
                        !!input.value && input.value === option.external_id
                          ? 'menu-option-instance.warnSameExternalIdAsOriginal'
                          : 'menu-option-instance.leaveExternalIdEmptyForDefault',
                    })}
                  >
                    <Input
                      {...input}
                      placeholder={option.external_id || intl.formatMessage({ id: 'menu.pos-id-placeholder' })}
                    />
                  </StyledInputContainer>
                )}
              </Field>
            </PaddedFormRow>
          )}

          {isMultichoiceOption && (
            <>
              <PaddedFormRow label={intl.formatMessage({ id: 'menu-option-instance.max-selections' })}>
                <Field<OptionBindingFormValues['maximumTotalSelections']> name="maximumTotalSelections" parse={v => v}>
                  {({ input, meta }) => (
                    <>
                      <StyledInputContainer>
                        <Input {...input} invalid={meta.invalid} />
                      </StyledInputContainer>
                      {!!meta.error && <ValidationError>{meta.error}</ValidationError>}
                    </>
                  )}
                </Field>
              </PaddedFormRow>

              <PaddedFormRow
                info={intl.formatMessage({ id: 'menu-option-instance.max-single-selections-description' })}
                label={intl.formatMessage({ id: 'menu-option-instance.max-single-selections' })}
              >
                <Field<OptionBindingFormValues['maximumSingleSelections']>
                  name="maximumSingleSelections"
                  parse={v => v}
                >
                  {({ input, meta }) => (
                    <>
                      <StyledInputContainer>
                        <Input {...input} invalid={meta.invalid} />
                        {optionChoicesWithMaxSelection.length > 0 && (
                          <MaximumSelectionsTooltip
                            content={
                              <TooltipContent>
                                <FormattedMessage id="menu-option-instance.max-single-selections-override-warning" />
                                <MaximumSelectionsList>
                                  {optionChoicesWithMaxSelection.map(choice => {
                                    if (!choice.multi_choice_config) {
                                      throw new Error('multi_choice_config field missing from option choice');
                                    }

                                    return (
                                      <li key={choice.id}>
                                        {localize(choice.name, primaryLanguage)}:{' '}
                                        {choice.multi_choice_config.selection_range.max}
                                      </li>
                                    );
                                  })}
                                </MaximumSelectionsList>
                              </TooltipContent>
                            }
                          >
                            <WarningIcon />
                          </MaximumSelectionsTooltip>
                        )}
                      </StyledInputContainer>
                      {!!meta.error && <ValidationError>{meta.error}</ValidationError>}
                    </>
                  )}
                </Field>
              </PaddedFormRow>

              <PaddedFormRow label={intl.formatMessage({ id: 'menu-option-instance.min-selections' })}>
                <Field<OptionBindingFormValues['minimumTotalSelections']> name="minimumTotalSelections" parse={v => v}>
                  {({ input, meta }) => (
                    <>
                      <StyledInputContainer>
                        <Input {...input} invalid={meta.invalid} />
                      </StyledInputContainer>
                      {!!meta.error && <ValidationError>{meta.error}</ValidationError>}
                    </>
                  )}
                </Field>
              </PaddedFormRow>

              <PaddedFormRow label={intl.formatMessage({ id: 'menu-option-instance.free-selections' })}>
                <Field<OptionBindingFormValues['freeSelections']> name="freeSelections" parse={v => v}>
                  {({ input, meta }) => (
                    <>
                      <StyledInputContainer>
                        <Input {...input} invalid={meta.invalid} />
                      </StyledInputContainer>
                      {!!meta.error && <ValidationError>{meta.error}</ValidationError>}
                    </>
                  )}
                </Field>
              </PaddedFormRow>
            </>
          )}

          <PaddedFormRow label={intl.formatMessage({ id: 'menu-option-instance.requiresOptionValue' })}>
            <Field<OptionBindingFormValues['useRequiredSelections']> name="useRequiredSelections">
              {({ input }) => (
                <InputSwitch
                  checked={input.value}
                  onChange={() => {
                    input.onChange(!input.value);
                  }}
                />
              )}
            </Field>
            {values.useRequiredSelections && (
              <FieldArray name="requiredSelections">
                {({ fields }) => (
                  <>
                    {fields.map((name: string, index: number) => (
                      <Field<RequiredSelection> name={name} key={index}>
                        {({ input }) => (
                          <RequiredSelectionWrapper>
                            <RequiredSelectionRow>
                              <RequiredSelectionLabel>
                                <FormattedMessage id="menu-option-instance.restrictOption" />
                              </RequiredSelectionLabel>

                              <RequiredSelectionSelect
                                value={input.value.option}
                                options={requiredSelectionOptions}
                                onChange={optionId => {
                                  input.onChange({ option: optionId, choice: '' });
                                }}
                              />

                              <RemoveRequiredSelection onClick={() => fields.remove(index)}>
                                <Trashbin />
                              </RemoveRequiredSelection>
                            </RequiredSelectionRow>

                            <RequiredSelectionRow>
                              <RequiredSelectionLabel>
                                <FormattedMessage id="menu-option-instance.restrictValue" />
                              </RequiredSelectionLabel>

                              <RequiredSelectionSelect
                                value={input.value.choice}
                                options={getOptionChoiceOptions(input.value.option, otherItemOptions, primaryLanguage)}
                                onChange={choiceId => input.onChange({ ...input.value, choice: choiceId })}
                              />
                            </RequiredSelectionRow>
                          </RequiredSelectionWrapper>
                        )}
                      </Field>
                    ))}
                    <AddRequiredSelection onClick={() => fields.push(DEFAULT_OPTION)}>
                      <FormattedMessage id="menu-option-instance.addRestriction" />
                    </AddRequiredSelection>
                  </>
                )}
              </FieldArray>
            )}
          </PaddedFormRow>
        </form>
      )}
    </Form>
  );
};

export default OptionBindingForm;
